/**
 * ShiftMetricsTable — the transposed metrics grid.
 *
 * METRICS DOWN, SHIFTS ACROSS. Every other report table in the portal is
 * row-per-record, and this one deliberately is not: the approved metric set is
 * eleven measures against three shifts, and a 3-row x 11-column table cannot be
 * read — the reader would scan sideways to compare two numbers that belong to
 * the same question. Transposed it is 11 x 3, each metric on one line, so the
 * three shifts compare by eye. That comparison is the entire job of this report.
 *
 * There is precedent: the corporate dashboard already models shifts as columns
 * (`total_events_1st` / `_2nd` / `_3rd` in mysql_report.py).
 *
 * No sorting. Shifts are a fixed chronological sequence; letting a click reorder
 * the columns would move Night next to Day between two runs of the same report.
 */

import InfoPopover from '../../../../components/v2/InfoPopover.jsx';
import {
    DASH,
    formatCount,
    formatDurationSeconds,
    formatMinutes,
    formatPct,
    formatRate,
    formatSignedPct,
    shiftColor,
    shiftHourLabels,
} from '../../../../utils/shiftFormat.js';

/* Row definitions. `get` returns the display string; `tone` optionally returns
   'good' | 'bad' | '' so a cell can carry a colour without the row having to
   know how cells are rendered. */
const METRICS = [
    {
        key: 'hours',
        label: 'Hours covered',
        tip: 'Which hours of the day this shift is assigned, from this campus’s shift settings',
        get: (r, ctx) => ctx.hourLabels[r.shift_name] || DASH,
        muted: true,
    },
    {
        key: 'total_events',
        label: 'Total Events',
        tip: 'Acknowledged events that started during this shift',
        get: (r) => formatCount(r.total_events),
        strong: true,
        /* `raw` feeds the magnitude bar. Kept separate from `get` because `get`
           returns a display string and the bar needs a number. */
        raw: (r) => r.total_events,
        bar: true,
    },
    {
        key: 'events_per_shift_hour',
        label: 'Events / Hour',
        /* The reason this row exists. Total Events is not comparable across
           shifts of unequal length, and a customised hour map often is not
           8/8/8, so "Day had the most events" can just mean "Day is the longest
           shift". */
        tip: 'Events divided by the hours this shift was on duty across the range — the figure that makes shifts of different lengths comparable',
        get: (r) => formatRate(r.events_per_shift_hour),
        child: true,
    },
    {
        key: 'events_per_day',
        label: 'Events / Day',
        tip: 'Events divided by the number of days in the range',
        get: (r) => formatRate(r.events_per_day),
        child: true,
    },
    {
        key: 'avg_response_time',
        label: 'Avg Response',
        tip: 'Average minutes from the event starting to the device being cleared',
        get: (r) => formatMinutes(r.avg_response_time),
        unit: 'min',
        strong: true,
        raw: (r) => r.avg_response_time,
        /* Longer bar = slower, so the bar is deliberately NOT tinted good/bad.
           Judgement lives on the vs Campus Avg row below, which knows what
           "better" means. This one is magnitude only. */
        bar: true,
    },
    {
        key: 'response_delta_pct',
        label: 'vs Campus Avg',
        tip: 'How this shift’s average response compares with the campus average for the same events. Positive is slower.',
        get: (r) => formatSignedPct(r.response_delta_pct),
        child: true,
        raw: (r) => r.response_delta_pct,
        /* Signed, so it gets a bar centred on zero rather than one growing from
           the right edge: the question is which side of the campus average the
           shift falls on, and by how much. */
        diverging: true,
        /* Faster than the campus is good, slower is bad. The zero case gets no
           tone rather than an arbitrary one. */
        tone: (r) => {
            const v = r.response_delta_pct;
            if (v === null || v === undefined) return '';
            if (v <= -5) return 'good';
            if (v >= 5) return 'bad';
            return '';
        },
    },
    {
        key: 'over_campus_avg_pct',
        label: 'Above Campus Avg',
        /* Response times are right-skewed, so this sits near 30-40% rather than
           50%. The campus figure is rendered in the row's own footnote for
           exactly that reason. */
        tip: 'Share of this shift’s events that took longer than the campus average. Read it against the campus figure below, not against 50% — response times are skewed, so the campus itself sits well under half.',
        get: (r) => formatPct(r.over_campus_avg_pct),
        child: true,
    },
    {
        key: 'p90_response_seconds',
        label: 'p90 Response',
        tip: '90% of responses were faster than this. More robust than Max, which is one event.',
        get: (r, ctx) => (r.p90_suppressed
            ? DASH
            : formatDurationSeconds(r.p90_response_seconds)),
        /* A dash here is a suppression, not missing data, so it gets its own
           explanation rather than looking like a gap (plan 5d). */
        cellTip: (r, ctx) => (r.p90_suppressed
            ? `Not shown: only ${formatCount(r.response_measurable_events)} measurable `
              + `${r.response_measurable_events === 1 ? 'response' : 'responses'}, below the `
              + `${ctx.p90Min} needed before the 90th percentile differs from the slowest single event`
            : ''),
    },
    {
        key: 'max_response_seconds',
        label: 'Max Response',
        tip: 'The single slowest response in this shift',
        get: (r) => formatDurationSeconds(r.max_response_seconds),
    },
    {
        key: 'min_response_seconds',
        label: 'Min Response',
        /* Kept as asked but sitting low, and the tooltip says why it reads as it
           does — it will be a few seconds on almost every shift, because
           accidental presses get cleared instantly. */
        tip: 'The single fastest response. Usually a few seconds, because an accidental press is cleared immediately — p90 is the column that carries the signal.',
        get: (r) => formatDurationSeconds(r.min_response_seconds),
        muted: true,
    },
    {
        key: 'avg_service_time',
        label: 'Avg Service',
        tip: 'Average minutes from the device being cleared to the event being finished. Automatically closed events are excluded, so this is averaged over fewer events than Total Events.',
        get: (r) => formatMinutes(r.avg_service_time),
        unit: 'min',
        cellTip: (r) => (r.service_time_events !== r.total_events
            ? `Averaged over ${formatCount(r.service_time_events)} of `
              + `${formatCount(r.total_events)} events; the rest had no measurable service period`
            : ''),
    },
    {
        key: 'handoff_events',
        label: 'Handoff-Spanning',
        tip: 'Events that started in this shift but were acknowledged in another — an alert that outlived its shift',
        get: (r) => formatCount(r.handoff_events),
    },
];

/* Largest absolute value of a metric across the shifts, used to scale that
   row's bars. Per row, not per table: Total Events and Avg Response are orders
   of magnitude apart and a shared scale would flatten one of them to nothing. */
const rowPeak = (metric, records) => records.reduce((max, r) => {
    const v = metric.raw ? metric.raw(r) : null;
    return v === null || v === undefined ? max : Math.max(max, Math.abs(Number(v)));
}, 0);

/* Magnitude bar, anchored to the RIGHT so it shares an edge with the figure
   above it. A left-anchored bar in a right-aligned numeric column leaves a
   ragged gap between number and bar, which is the same "which column does this
   belong to" confusion the alignment fix addresses. */
const MagnitudeBar = ({ value, peak }) => {
    if (value === null || value === undefined || !peak) return null;
    const pct = Math.max(2, Math.min(100, (Math.abs(Number(value)) / peak) * 100));
    return (
        <span className="sr-bar" aria-hidden="true">
            <span className="sr-bar__fill" style={{ width: `${pct}%` }} />
        </span>
    );
};

/* Diverging bar for a signed percentage. Centre is the campus average, so the
   side carries the meaning and the length carries the size. Faster than campus
   reads green to the left, slower reads red to the right, matching the tone
   already applied to the number itself. */
const DivergingBar = ({ value, peak }) => {
    if (value === null || value === undefined || !peak) return null;
    const v = Number(value);
    /* 3% floor, not 1%: on a 64px track 1% is half a pixel and a shift
       sitting a few points off the campus average showed no bar at all.
       3% is ~2px -- enough to say "small" without overstating it. */
    const half = Math.max(3, Math.min(50, (Math.abs(v) / peak) * 50));
    const slower = v > 0;
    return (
        <span className="sr-dbar" aria-hidden="true">
            <span
                className={`sr-dbar__fill sr-dbar__fill--${slower ? 'bad' : 'good'}`}
                style={slower
                    ? { left: '50%', width: `${half}%` }
                    : { right: '50%', width: `${half}%` }}
            />
        </span>
    );
};

const ShiftMetricsTable = ({ records = [], summary = {}, hours = [] }) => {
    const hourLabels = shiftHourLabels(hours);
    const ctx = {
        hourLabels,
        p90Min: summary.p90_min_events ?? 10,
    };

    if (!records.length) {
        return (
            <div className="sr-card">
                <p className="sr-empty">No shifts are configured for this campus in this period.</p>
            </div>
        );
    }

    const rateRowsMasked = Boolean(summary.window_masked);

    return (
        <div className="sr-card">
            <div className="sr-table-wrap">
                <table className="dp-table sr-metrics">
                    <thead className="dp-thead">
                        <tr>
                            <th className="dp-th sr-metrics__rowhead" scope="col">Measure</th>
                            {records.map((r) => (
                                <th
                                    key={r.shift_name}
                                    className="dp-th sr-metrics__shifthead"
                                    scope="col"
                                    style={{ borderTopColor: shiftColor(r.shift_name) }}
                                >
                                    <span className="sr-shiftname">
                                        <span
                                            className="sr-swatch"
                                            style={{ background: shiftColor(r.shift_name) }}
                                            aria-hidden="true"
                                        />
                                        {r.shift_name}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {METRICS.map((m) => {
                            const isRate = m.key === 'events_per_shift_hour' || m.key === 'events_per_day';
                            const peak = m.bar || m.diverging ? rowPeak(m, records) : 0;
                            const rowClass = [
                                m.strong ? 'sr-metrics__row--strong' : '',
                                m.child ? 'sr-metrics__row--child' : '',
                            ].filter(Boolean).join(' ');
                            return (
                                <tr key={m.key} className={rowClass || undefined}>
                                    <th className="dp-td sr-metrics__rowhead" scope="row">
                                        {/* A popover, not a `title`. The native tooltip
                                            waits about a second and a click cancels it
                                            outright, so the ⓘ promised something that
                                            never arrived. This opens instantly on hover
                                            and pins on click, matching the ⓘ beside the
                                            page title. */}
                                        <InfoPopover body={m.tip} triggerClassName="sr-metric-label">
                                            {m.label}
                                            {m.unit ? <span className="sr-metric-unit"> ({m.unit})</span> : null}
                                        </InfoPopover>
                                    </th>
                                    {records.map((r) => {
                                        const cellTip = m.cellTip ? m.cellTip(r, ctx) : '';
                                        const tone = m.tone ? m.tone(r) : '';
                                        const value = isRate && rateRowsMasked ? DASH : m.get(r, ctx);
                                        return (
                                            <td
                                                key={r.shift_name}
                                                className={[
                                                    'dp-td', 'sr-metrics__cell',
                                                    m.muted ? 'sr-metrics__cell--muted' : '',
                                                    tone ? `sr-tone--${tone}` : '',
                                                ].filter(Boolean).join(' ')}
                                            >
                                                {/* Cells whose note explains the NUMBER
                                                    (a suppressed p90, a service average
                                                    over fewer events) carry the same
                                                    popover with a `*` marker instead of
                                                    an ⓘ, so it does not disturb the
                                                    tabular alignment of the figures. */}
                                                {cellTip ? (
                                                    <InfoPopover body={cellTip} marker="star" maxWidth={280}>
                                                        {value}
                                                    </InfoPopover>
                                                ) : value}
                                                {/* Bars only on the two headline rows and the
                                                    signed comparison. On all eleven they would
                                                    be wallpaper, and it is the contrast between
                                                    a row that has one and a row that does not
                                                    that makes the hierarchy readable. */}
                                                {m.bar && !(isRate && rateRowsMasked)
                                                    ? <MagnitudeBar value={m.raw(r)} peak={peak} />
                                                    : null}
                                                {m.diverging
                                                    ? <DivergingBar value={m.raw(r)} peak={peak} />
                                                    : null}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="sr-metrics__notes">
                {summary.campus_avg_response_time !== null && summary.campus_avg_response_time !== undefined ? (
                    <span className="sr-note">
                        {`Campus average response ${formatMinutes(summary.campus_avg_response_time)} min`}
                        {summary.over_campus_avg_pct !== null && summary.over_campus_avg_pct !== undefined
                            ? ` · ${formatPct(summary.over_campus_avg_pct)} of all events sit above it`
                            : ''}
                    </span>
                ) : null}
                {rateRowsMasked ? (
                    /* The rate rows would be wrong rather than imprecise under a
                       time-of-day mask: hours-on-duty comes from the shift map
                       while the mask admits only some of those hours. Withheld
                       with a reason instead of published wrong. */
                    <span className="sr-note sr-note--warn">
                        Events / Hour and Events / Day are not shown because a time-of-day
                        filter is applied — the hours a shift was on duty no longer matches
                        the hours included, so the rates would understate.
                    </span>
                ) : null}
            </div>
        </div>
    );
};

export default ShiftMetricsTable;
